import React, { useState, useEffect, useCallback, useRef } from 'react';

import {
  ArbreCapesSVG,
  LayerArbreIcon,
  ArbreContainer,
  IconWrapper,
  LayerWrapper,
  ExpandShrinkButton,
  ExpandButton,
  ShrinkButton,
  LayerCheckbox,
  LayerTitle,
  LayerDescription,
  LayerInfo
} from './ArbreCapesElements';

import { catalogObj as Data } from './Data';
import { createLayer, getLegendURL } from '../Utils/MapUtils';
import { getTranslatedKey } from '../Utils/TranslateUtils';
import { normalizeText } from '../Utils/FormatUtils';

const ArbreCapes = (props) => {
  let {
    indent, //indentacion del arbol
    shrinkAllCat, //booleano de los botones expandir/encoger todo
    shrinkAllExternal, //booleano de los botones expandir/encoger todo
    shrinkAllUser, //booleano de los botones expandir/encoger
    searchTextCat, //texto del buscador
    searchTextExternal, //texto del cargador de capas
    searchTextUser, //texto del buscador de user layers
    layer, //capa actual
    arrayLayers,
    arrayExternalLayers,
    map, //mapa
    tab,
    external, //booleano de si la capa es externa o no
    fontSizeEnabled,
    capabilities
  } = props || {};

  let [catChildrenHidden, setCatChildrenHidden] = useState(shrinkAllCat); //subcapas mostradas/ocultas del catalogo
  let [externalChildrenHidden, setExternalChildrenHidden] = useState(shrinkAllExternal); //subcapas mostradas/ocultas de externes
  let [userChildrenHidden, setUserChildrenHidden] = useState(shrinkAllUser); //subcapas mostradas/ocultas de externes
  let [showInfo, setShowInfo] = useState(false); //informacion de capa mostrada/oculta
  let [showLayer, setShowLayer] = useState(layer.isShown()); //mostrar capa segun la busqueda
  let [isChecked, setChecked] = useState(layer.isChecked()); //valor de la checkbox
  let [forceRender, setForceRender] = useState(false);
  const prevSearchTextCatLength = useRef(searchTextCat.length);

  function useForceUpdate() {
    const [value, setValue] = useState(0); // integer state
    return () => setValue((value) => value + 1); // update state to force render
  }

  const isInLayers = useCallback(
    (layer) => {
      return arrayLayers.some((lay) => lay.getName() === layer.getName());
    },
    [arrayLayers]
  );

  const isAllChecked = useCallback(() => {
    if (layer.hasChilds()) {
      let numUncheckeds = 0;
      for (let i = 0; i < layer.getNumChilds(); ++i) {
        let childLayer = layer.getChildLayer(i);
        if (!childLayer.isChecked()) numUncheckeds++;
        if (childLayer.hasChilds()) {
          for (let j = 0; j < childLayer.getNumChilds(); ++j) {
            if (!childLayer.getChildLayer(j).isChecked()) numUncheckeds++;
          }
        }
      }
      if (numUncheckeds === 0) {
        return true;
      } else {
        return false;
      }
    } else {
      return layer.isChecked();
    }
  }, [layer]);

  /**
   * @desc funcion para desmarcar una capa padre cuando no tenga ninguna hija checked y marcar el padre cuando haya alguna checked
   */
  const toggleCheckbox = useCallback(() => {
    let numUncheckeds = 0;
    let totalChilds = 0;
    //si es una capa padre y esta checked, recorremos las hijas
    if (layer.hasChilds()) {
      totalChilds += layer.getNumChilds();
      for (let i = 0; i < layer.getNumChilds(); ++i) {
        let childLayer = layer.getChildLayer(i);
        //si esta checked, sumamos al total de capas sin checkear
        if (!isInLayers(childLayer)) numUncheckeds++;
        //recorremos las hijas si tiene
        if (childLayer.hasChilds()) {
          totalChilds += childLayer.getNumChilds();
          for (let j = 0; j < childLayer.getNumChilds(); ++j) {
            //si esta checked, sumamos al total de capas sin checkear
            if (!isInLayers(childLayer.getChildLayer(j))) numUncheckeds++;
          }
        }
      }
      //si estan todas unchecked, actualizamos la checkbox a falso
      if (numUncheckeds === totalChilds && layer.isChecked()) {
        setChecked(false);
        layer.setChecked(false);
        props.handleLayerChange();
        props.updateChecks();
      }
      //si hay alguna checked, padre checked tambien
      else if (numUncheckeds !== totalChilds && !layer.isChecked()) {
        setChecked(true);
        layer.setChecked(true);
        props.handleLayerChange();
        props.updateChecks();
      }
    }
  }, [isInLayers, layer, props]);

  /**
   * @desc hook para desmarcar las capas cuando se borran de la leyenda
   */
  useEffect(() => {
    let isLayer;
    if (!layer.hasChilds()) {
      //buscamos en el array de capas de la leyenda
      isLayer = arrayLayers.some((l) => l.getTitle() + l.getName() === layer.getTitle() + layer.getName());
      //si no la encontramos, la descheckeamos
      if (!isLayer) {
        setChecked(false);
        layer.setChecked(false);
      } else {
        //si la encontramos la checkeamos
        setChecked(true);
        layer.setChecked(true);
      }
    }
    toggleCheckbox();
  }, [arrayLayers, layer, toggleCheckbox]);

  /**
   *
   * @desc hook para rerenderizar las capas mostradas del catalogo al cambiar el estado de la variable shrinkAllCat
   */
  useEffect(() => {
    if (shrinkAllCat) {
      setCatChildrenHidden(true);
    } else {
      setCatChildrenHidden(false);
    }
  }, [shrinkAllCat]);

  /**
   *
   * @desc hook para rerenderizar las capas mostradas del loader al cambiar el estado de la variable shrinkAllExternal
   */
  useEffect(() => {
    if (shrinkAllExternal) {
      setExternalChildrenHidden(true);
    } else {
      setExternalChildrenHidden(false);
    }
  }, [shrinkAllExternal]);

  /**
   *
   * @desc hook para rerenderizar las capas mostradas del catalogo al cambiar el estado de la variable shrinkAllUser
   */
  useEffect(() => {
    if (shrinkAllUser) {
      setUserChildrenHidden(true);
    } else {
      setUserChildrenHidden(false);
    }
  }, [shrinkAllUser]);

  /**
   *
   * @param {*} layer
   * @param {*} searchText
   * @desc funcio que filtra les capes segons el buscat al cercador
   */
  function filterSearch(layer, searchText) {
    let title = normalizeText(layer.getTitle());
    let name = normalizeText(layer.getName());
    let abstract = normalizeText(layer.getAbstract());
    let text = normalizeText(searchText);
    if (text.length > 2) {
      return title?.includes(text) || name?.includes(text) || abstract?.includes(text);
    } else {
      return title || name || abstract;
    }
    // return title?.includes(text) || name?.includes(text) || abstract?.includes(text);
  }

  useEffect(() => {
    const currentLength = searchTextCat.length;

    if (currentLength > 2 && shrinkAllCat) {
      props.onExpandShrinkBtnClick('cat', 'expand');
    } else if (prevSearchTextCatLength.current === 1 && currentLength === 0) {
      props.onExpandShrinkBtnClick('cat', 'shrink');
    }

    prevSearchTextCatLength.current = currentLength;
  }, [searchTextCat, props, shrinkAllCat]);

  /**
   *
   * @desc hook para renderizar las capas filtradas en el buscador
   */
  useEffect(() => {
    const searchText = tab === 'cataleg' ? searchTextCat : tab === 'usuari' ? searchTextUser : searchTextExternal;

    //miramos si es una capa raíz y si cumple la condición de búsqueda
    if (!layer.hasChilds() && filterSearch(layer, searchText)) {
      setShowLayer(true);
      layer.setShown(true);
    } else {
      setShowLayer(false);
      layer.setShown(false);
    }
    //recorremos las capas padre para mostrar a los padres de las capas que cumplen la condicion
    if (layer.hasChilds()) {
      for (let i = 0; i < layer.getNumChilds(); ++i) {
        //el hijo cumple la condición
        if (filterSearch(layer.getChildLayer(i), searchText)) {
          setShowLayer(true);
          layer.setShown(true);
        }
        for (let j = 0; layer.getChildLayer(i).hasChilds() && j < layer.getChildLayer(i).getNumChilds(); ++j) {
          //el hijo raiz cumple la condición
          if (filterSearch(layer.getChildLayer(i).getChildLayer(j), searchText)) {
            setShowLayer(true);
            layer.setShown(true);
          }
        }
      }
    }
  }, [layer, tab, searchTextUser, searchTextExternal, searchTextCat]);

  /**
   *
   * @desc función para actualizar el estado de expandir/encoger de las capas al pulsar el boton
   */
  function onExpandShrinkBtnClick() {
    if (tab === 'cataleg') setCatChildrenHidden(!catChildrenHidden);
    else if (tab === 'externes') setExternalChildrenHidden(!externalChildrenHidden);
    else if (tab === 'usuari') setUserChildrenHidden(!userChildrenHidden);

    if (
      layer.hasStyles() &&
      layer.getCurrentStyle() !== undefined &&
      layer.getCurrentStyle().getNumRules() !== undefined &&
      layer.getCurrentStyle().getNumRules() > 0
    ) {
      setShowInfo(!showInfo);
    }
  }

  /**
   *
   * @desc función para procesar las capas al checkearlas o descheckearlas del catalogo
   */
  async function processSelectedLayer(layer, toCheck) {
    //si no esta mostrada por el filtro, no la procesamos
    if (layer.isShown()) {
      //miramos si es una capa que estaba checked o no
      if (toCheck) {
        //miramos si la capa tiene una capa de open layers asignada
        if (layer.getOLLayer() !== undefined && !layer.hasError()) {
          //si la tiene, la añadimos al principio del array de layers de la leyenda
          arrayLayers.unshift(layer);
          //la añadimos al mapa
          await map.addLayer(layer.getOLLayer());
        } else {
          //en caso de no tener capa de openlayers asignada o de que esta tenga error, la creamos
          await createLayer(layer, map, arrayLayers, external, undefined);

          if (!layer.isExternal()) {
            layer.setBbox(layer, capabilities);
          }
        }
        if (external) arrayExternalLayers.push(layer);
        //si es una capa hija, actualizamos el estado de checked
        await layer.setChecked(true);
      } else {
        //miramos si la capa está en el array de layers de la leyenda
        let index = arrayLayers.findIndex((l) => l.getTitle() + l.getName() === layer.getTitle() + layer.getName());
        if (index !== -1) {
          //si la hemos encontrado, la borramos del mapa y la quitamos del array de capas de la leyenda
          await map.removeLayer(arrayLayers[index].getOLLayer());
          arrayLayers[index].getOLLayer().setOpacity(1);
          arrayLayers[index].setStyleSelected(0);
          arrayLayers[index].setOLLayer(undefined);

          arrayLayers.splice(index, 1);
          if (external) {
            let extLayer = arrayExternalLayers.findIndex((l) => l.getTitle() + l.getName() === layer.getTitle() + layer.getName());
            arrayExternalLayers.splice(extLayer, 1);
          }
        }
        await layer.setChecked(false);
      }
      //notificamos a la capa padre de que hay cambios y hacemos un refresh
      await props.handleLayerChange();
      await props.updateChecks();
    }
  }

  /**
   * @desc funcion que se llama al darle a la checkbox de una capa
   */
  async function selectLayer() {
    //caso layer sin checkear
    if (!layer.isChecked()) {
      //caso de checkear a una capa sin hijas
      if (!layer.hasChilds()) {
        //las capas que no son hijas no las queremos pintar
        if (!arrayLayers.some((l) => l.getTitle() + l.getName() === layer.getTitle() + layer.getName())) {
          await processSelectedLayer(layer, true);
        }
        //caso de checkear una capa con hijas
      } else {
        //recorremos todas sus hijas
        [...layer.getChildLayers()].reverse().forEach(async (childLayer) => {
          if (!arrayLayers.some((l) => l.getTitle() + l.getName() === childLayer.getTitle() + childLayer.getName())) {
            if (!childLayer.hasChilds()) {
              await processSelectedLayer(childLayer, true);
            } else {
              [...childLayer.getChildLayers()].reverse().forEach(async (grandChildLayer) => {
                if (!arrayLayers.some((l) => l.getTitle() + l.getName() === grandChildLayer.getTitle() + grandChildLayer.getName())) {
                  await processSelectedLayer(grandChildLayer, true);
                }
              });
              await childLayer.setChecked(true);
            }
          }
        });
      }
      //actualizamos el estado de la checkbox
      setChecked((state) => true); //esto se hace para re-renderizar, realmente no se usa
      await layer.setChecked(true);
      //caso layer checkeada
    } else {
      //si no tiene hijas, la buscamos en el array de la leyenda
      if (!layer.hasChilds()) {
        await processSelectedLayer(layer, false);
      } else {
        //si tiene hijas, recorremos todas las hijas y las buscamos en el array de la leyenda
        for (let i = 0; layer.hasChilds() && i < layer.getNumChilds(); ++i) {
          if (!layer.getChildLayer(i).hasChilds()) {
            await processSelectedLayer(layer.getChildLayer(i), false);
          } else {
            let childLayer = layer.getChildLayer(i);
            for (let j = 0; childLayer.hasChilds() && j < childLayer.getNumChilds(); ++j) {
              await processSelectedLayer(childLayer.getChildLayer(j), false);
            }
            //actualizamos el estado de la checkbox
            await childLayer.setChecked(false);
          }
        }
        setChecked((state) => false);
        await layer.setChecked(false);
      }
      //actualizamos el estado de la checkbox y notificamos al padre de que hay cambios en los estados
      setChecked((state) => false);
      await layer.setChecked(false);
    }
  }

  const forceUpdate = useForceUpdate();

  //crear el estilo de la capa
  function createStyle() {
    layer.generateUIStyle(layer.getStyleSelected(), layer, forceUpdate);
  }

  //Lazy loading de los estilos
  const ref = useRef();

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        showLayer && !layer.hasChildsOrRules() && !layer.hasRule(layer.getStyleSelected()) && createStyle();
      }
    });

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [layer, ref, showLayer]);

  return (
    <>
      <div ref={ref}>
        {showLayer && showInfo && !layer.hasRule(layer.getStyleSelected()) && createStyle()}
        {showLayer && (
          <ArbreContainer indent={indent} id={'layer_' + layer.getName()} hasRules={layer.hasChildsOrRules()}>
            <LayerWrapper id={'layerHeader_' + layer.getTitle()} aria-label={layer.getTitle()} hasRules={layer.hasChildsOrRules()} indent={indent}>
              {layer.hasChildsOrRules() && (
                <ExpandShrinkButton aria-label={'expand or shrink' + layer.getTitle()} onClick={() => onExpandShrinkBtnClick()}>
                  {(tab === 'cataleg' && catChildrenHidden && layer.hasChilds()) || (tab === 'cataleg' && !showInfo && !layer.hasChilds()) ? (
                    <ExpandButton aria-label={getTranslatedKey('aria_abreExpandir')} fontSizeEnabled={fontSizeEnabled}>
                      <path
                        xmlns="http://www.w3.org/2000/svg"
                        d="M17,13H13V17H11V13H7V11H11V7H13V11H17M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"
                      />
                    </ExpandButton>
                  ) : (tab === 'cataleg' && !catChildrenHidden && layer.hasChilds()) || (tab === 'cataleg' && showInfo && !layer.hasChilds()) ? (
                    <ShrinkButton aria-label={getTranslatedKey('aria_abreContreure')} fontSizeEnabled={fontSizeEnabled}>
                      <path d="M17,13H7V11H17M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
                    </ShrinkButton>
                  ) : tab === 'externes' && externalChildrenHidden ? (
                    <ExpandButton aria-label={getTranslatedKey('aria_abreExpandir')} fontSizeEnabled={fontSizeEnabled}>
                      <path
                        xmlns="http://www.w3.org/2000/svg"
                        d="M17,13H13V17H11V13H7V11H11V7H13V11H17M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"
                      />
                    </ExpandButton>
                  ) : tab === 'usuari' && userChildrenHidden ? (
                    <ExpandButton aria-label={getTranslatedKey('aria_abreExpandir')} fontSizeEnabled={fontSizeEnabled}>
                      <path
                        xmlns="http://www.w3.org/2000/svg"
                        d="M17,13H13V17H11V13H7V11H11V7H13V11H17M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"
                      />
                    </ExpandButton>
                  ) : (
                    <ShrinkButton aria-label={getTranslatedKey('aria_abreContreure')} fontSizeEnabled={fontSizeEnabled}>
                      <path d="M17,13H7V11H17M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
                    </ShrinkButton>
                  )}
                </ExpandShrinkButton>
              )}
              <LayerCheckbox
                type="checkbox"
                indent={indent}
                onChange={async () => {
                  await selectLayer();
                  setForceRender(!forceRender);
                }}
                checked={layer.isChecked()}
                img={Data.checkboxIcon}
                aria-label={getTranslatedKey('aria_abreCheckbox')}
                fontSizeEnabled={fontSizeEnabled}
              />
              {!layer.hasChilds() && layer.isExternal() && getLegendURL(layer) !== false ? (
                <IconWrapper>
                  <ArbreCapesSVG fontSizeEnabled={fontSizeEnabled} id="NoRep" viewBox="0 0 512 512" fill="white">
                    <g>
                      <rect x="10.5" y="357.7" class="st2" width="145" height="145" />
                      <rect x="10.5" y="183.9" class="st2" width="145" height="145" />
                      <rect x="182.7" y="357.7" class="st2" width="145" height="145" />
                      <rect x="182.7" y="183.9" class="st3" width="145" height="145" />
                      <rect x="10.5" y="9.9" class="st3" width="145" height="145" />
                      <rect x="182.7" y="9.8" class="st3" width="145" height="145" />
                      <rect x="356.5" y="184.7" class="st3" width="145" height="145" />
                      <rect x="356.5" y="356.7" class="st3" width="145" height="145" />
                      <polygon class="st2" points="9.9,8.2 505.4,8.2 505.4,501.8 356,501.8 356,326 184.9,326 184.9,155 9.9,155  " />
                    </g>
                  </ArbreCapesSVG>
                </IconWrapper>
              ) : !layer.hasChilds() && layer.hasChildsOrRules() ? (
                <IconWrapper>
                  <ArbreCapesSVG fontSizeEnabled={fontSizeEnabled} fill="white" viewBox="0 0 17.2 14.3">
                    <path d="M0.1,0.1v14h17v-14H0.1 M4.1,2.1v2h-2v-2H4.1 M2.1,8.1v-2h2v2H2.1 M2.1,10.1h2v2h-2V10.1 M15.1,12.1h-9v-2h9  V12.1 M15.1,8.1h-9v-2h9V8.1 M15.1,4.1h-9v-2h9V4.1z" />
                  </ArbreCapesSVG>
                </IconWrapper>
              ) : !layer.hasChilds() &&
                (layer.getRule(layer.getStyleSelected()) == undefined || layer.getRule(layer.getStyleSelected()) == 'error') ? (
                <IconWrapper>
                  <ArbreCapesSVG id="NoRep" viewBox="0 0 512 512" fill="white" fontSizeEnabled={fontSizeEnabled}>
                    <g>
                      <rect x="10.5" y="357.7" class="st2" width="145" height="145" />
                      <rect x="10.5" y="183.9" class="st2" width="145" height="145" />
                      <rect x="182.7" y="357.7" class="st2" width="145" height="145" />
                      <rect x="182.7" y="183.9" class="st3" width="145" height="145" />
                      <rect x="10.5" y="9.9" class="st3" width="145" height="145" />
                      <rect x="182.7" y="9.8" class="st3" width="145" height="145" />
                      <rect x="356.5" y="184.7" class="st3" width="145" height="145" />
                      <rect x="356.5" y="356.7" class="st3" width="145" height="145" />
                      <polygon class="st2" points="9.9,8.2 505.4,8.2 505.4,501.8 356,501.8 356,326 184.9,326 184.9,155 9.9,155  " />
                    </g>
                  </ArbreCapesSVG>
                </IconWrapper>
              ) : (
                <LayerArbreIcon isParent={layer.hasChildsOrRules()}>{layer.getRule(layer.getStyleSelected())}</LayerArbreIcon>
              )}
              <LayerTitle
                allChecked={isAllChecked()}
                checked={!layer.hasChilds() && layer.isChecked()}
                title={layer.getTitle()}
                onClick={() => {
                  if (layer.hasChilds()) {
                    onExpandShrinkBtnClick();
                  } else {
                    selectLayer();
                  }
                }}
                fontSizeEnabled={fontSizeEnabled}>
                {layer.getTitle()}
              </LayerTitle>
            </LayerWrapper>
            {layer.hasStyles() &&
              layer.getCurrentStyle() !== undefined &&
              layer.getCurrentStyle().getNumRules() !== undefined &&
              layer.getCurrentStyle().getNumRules() > 0 &&
              showInfo && (
                <LayerInfo id={'layerInfo_' + layer.getTitle()} fontSizeEnabled={fontSizeEnabled}>
                  <LayerDescription
                    title={layer.getCurrentStyle() && layer.getCurrentStyle().getTitle() !== undefined ? layer.getCurrentStyle().getTitle() : null}
                    fontSizeEnabled={fontSizeEnabled}>
                    {layer.getCurrentStyle() && layer.getCurrentStyle().getTitle() !== undefined ? layer.getCurrentStyle().getTitle() : null}
                  </LayerDescription>
                  {layer.getRule(layer.getStyleSelected())}
                </LayerInfo>
              )}
          </ArbreContainer>
        )}

        {tab === 'cataleg' &&
          layer.getChildLayers() &&
          layer.getChildLayers() !== undefined &&
          layer.getNumChilds() > 0 &&
          !catChildrenHidden &&
          layer.getChildLayers().map((branch, index) => {
            return (
              <ArbreCapes
                capabilities={capabilities}
                tab={tab}
                map={map}
                key={index}
                layer={branch}
                indent={indent + 1}
                shrinkAllExternal={shrinkAllExternal}
                shrinkAllCat={shrinkAllCat}
                searchTextCat={searchTextCat}
                searchTextExternal={searchTextExternal}
                searchTextUser={searchTextUser}
                handleLayerChange={props.handleLayerChange}
                onExpandShrinkBtnClick={props.onExpandShrinkBtnClick}
                updateChecks={props.updateChecks}
                arrayLayers={arrayLayers}
                arrayExternalLayers={arrayExternalLayers}
                external={external}
                fontSizeEnabled={fontSizeEnabled}
              />
            );
          })}
        {tab === 'externes' &&
          layer.getChildLayers() &&
          layer.getChildLayers() !== undefined &&
          layer.getNumChilds() > 0 &&
          !externalChildrenHidden &&
          layer.getChildLayers().map((branch, index) => {
            return (
              <ArbreCapes
                capabilities={capabilities}
                tab={tab}
                map={map}
                key={index}
                layer={branch}
                indent={indent + 1}
                shrinkAllExternal={shrinkAllExternal}
                shrinkAllCat={shrinkAllCat}
                searchTextCat={searchTextCat}
                searchTextUser={searchTextUser}
                searchTextExternal={searchTextExternal}
                handleLayerChange={props.handleLayerChange}
                updateChecks={props.updateChecks}
                onExpandShrinkBtnClick={props.onExpandShrinkBtnClick}
                arrayLayers={arrayLayers}
                arrayExternalLayers={arrayExternalLayers}
                external={external}
                fontSizeEnabled={fontSizeEnabled}
              />
            );
          })}
        {tab === 'usuari' &&
          layer.getChildLayers() &&
          layer.getChildLayers() !== undefined &&
          layer.getNumChilds() > 0 &&
          !userChildrenHidden &&
          layer.getChildLayers().map((branch, index) => {
            return (
              <ArbreCapes
                capabilities={capabilities}
                tab={tab}
                map={map}
                key={index}
                layer={branch}
                indent={indent + 1}
                shrinkAllUser={shrinkAllUser}
                shrinkAllCat={shrinkAllCat}
                searchTextCat={searchTextCat}
                searchTextUser={searchTextUser}
                searchTextExternal={searchTextExternal}
                handleLayerChange={props.handleLayerChange}
                updateChecks={props.updateChecks}
                onExpandShrinkBtnClick={props.onExpandShrinkBtnClick}
                arrayLayers={arrayLayers}
                arrayExternalLayers={arrayExternalLayers}
                external={external}
                fontSizeEnabled={fontSizeEnabled}
              />
            );
          })}
      </div>
    </>
  );
};

export default ArbreCapes;
